import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { RivhitCustomer, SelectedCustomer } from './types';

interface CustomerStepProps {
  selected: SelectedCustomer | null;
  onSelect: (customer: SelectedCustomer) => void;
  customers: RivhitCustomer[];
  loading: boolean;
}

function fuzzyMatch(customer: RivhitCustomer, query: string): boolean {
  const q = query.toLowerCase();
  return [
    customer.first_name,
    customer.last_name,
    customer.phone,
    customer.email,
  ].some((field) => field?.toLowerCase().includes(q));
}

export default function CustomerStep({
  selected,
  onSelect,
  customers,
  loading,
}: CustomerStepProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isEditing, setIsEditing] = useState(!selected);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Client-side fuzzy filtering — show all when no query
  const filtered = useMemo(() => {
    if (!debouncedQuery.trim()) return customers;
    return customers.filter((c) => fuzzyMatch(c, debouncedQuery));
  }, [customers, debouncedQuery]);

  const { visible: visibleCustomers, hasMore, sentinelRef } = useInfiniteScroll(filtered, 50);

  const selectCustomer = useCallback(
    (customer: RivhitCustomer) => {
      const name = [customer.first_name, customer.last_name]
        .filter(Boolean)
        .join(' ');
      onSelect({
        customer_id: customer.customer_id,
        display_name: name,
        is_free_text: false,
      });
      setQuery('');
      setIsEditing(false);
    },
    [onSelect],
  );

  const handleFreeText = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed) {
      onSelect({
        customer_id: null,
        display_name: trimmed,
        is_free_text: true,
      });
      setIsEditing(false);
    }
  }, [query, onSelect]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFreeText();
    }
  };

  const handleChange = () => {
    setIsEditing(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Selected customer card
  if (selected && !isEditing) {
    return (
      <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-lg font-semibold text-blue-900 truncate">
            {selected.display_name}
          </p>
          {selected.is_free_text ? (
            <p className="text-sm text-blue-600 mt-1">
              {t('createDocument.customer.freeTextLabel', 'Custom customer name')}
            </p>
          ) : (
            (() => {
              const match = customers.find(
                (c) => c.customer_id === selected.customer_id,
              );
              return match ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-blue-700">
                  {match.phone && <span>📞 {match.phone}</span>}
                  {match.email && <span>✉️ {match.email}</span>}
                  {match.city && <span>📍 {match.city}</span>}
                </div>
              ) : null;
            })()
          )}
        </div>
        <button
          type="button"
          onClick={handleChange}
          className="shrink-0 px-4 py-2 rounded-lg bg-white border border-blue-300 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
        >
          {t('createDocument.customer.change', 'Change')}
        </button>
      </div>
    );
  }

  // Search input + scrollable list
  return (
    <div className="space-y-3">
      <div className="relative">
        <span className="absolute inset-y-0 ltr:left-4 rtl:right-4 flex items-center pointer-events-none text-gray-400 text-lg">
          🔍
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t(
            'createDocument.customer.searchPlaceholder',
            'Search by name, phone, or email...',
          )}
          className="w-full rounded-xl border border-gray-300 p-4 ltr:pl-12 rtl:pr-12 text-lg
            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400
            transition-shadow bg-white"
          autoComplete="off"
        />
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-400 ltr:text-left rtl:text-right">
        {loading ? (
          <span className="inline-flex items-center gap-1 text-blue-400">
            <span className="animate-spin inline-block h-3 w-3 border-2 border-blue-300 border-t-blue-500 rounded-full" />
            {t('createDocument.customer.loadingInline', 'Loading customers...')}
          </span>
        ) : (
          <span>
            {filtered.length} {t('customers.resultsFound', 'results found')}
            {debouncedQuery.trim() && ` · ${t('createDocument.customer.freeTextHint', 'Or type a custom name and press Enter')}`}
          </span>
        )}
      </div>

      {/* Scrollable customer list */}
      <div
        ref={listRef}
        className="max-h-80 overflow-y-auto rounded-xl border border-gray-200 bg-white divide-y divide-gray-100"
      >
        {filtered.length === 0 && !loading && (
          <div className="py-8 text-center text-gray-400 text-sm">
            {t('createDocument.customer.noResults', 'No matching customers found')}
          </div>
        )}
        {visibleCustomers.map((customer) => {
          const name = [customer.first_name, customer.last_name]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={customer.customer_id}
              onClick={() => selectCustomer(customer)}
              className="py-3 px-4 cursor-pointer hover:bg-blue-50 transition-colors"
            >
              <p className="font-medium text-gray-900">{name}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-sm text-gray-500">
                {customer.phone && <span>📞 {customer.phone}</span>}
                {customer.email && <span>✉️ {customer.email}</span>}
                {customer.city && <span>📍 {customer.city}</span>}
              </div>
            </div>
          );
        })}
        {hasMore && (
          <div ref={sentinelRef} className="py-2 text-center text-xs text-gray-400">
            {visibleCustomers.length} / {filtered.length}
          </div>
        )}
      </div>
    </div>
  );
}
